import {notify} from '@/plugins/notification';
import i18n from '@/i18n';

const RELOAD_COUNT_KEY = 'intilly:chunk-reload-count';
const MAX_RELOADS = 1;
const SETTLED_GRACE_MS = 10_000;

const CHUNK_ERROR_PATTERNS = [
  /Loading chunk \S+ failed/i,
  /Loading CSS chunk \S+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

const CHUNK_PATH_RE = /^\/(js|css)\/[^/]+\.[a-f0-9]{6,}\.(js|css)(\?|$)/;

export function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const message = error.message || String(error);
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

function isOwnAppChunk(src) {
  if (!src) return false;
  try {
    const url = new URL(src, window.location.origin);
    if (url.origin !== window.location.origin) return false;
    return CHUNK_PATH_RE.test(url.pathname);
  } catch {
    return false;
  }
}

function notifyManualReload(error) {
  console.error('[chunkReload] reload budget exhausted, asking user to refresh manually', error);
  if (typeof notify === 'function') {
    notify(i18n.global.t('Update detected — please refresh the page'), 'red', 8000);
  }
}

function reloadOnceForChunkError(error) {
  const count = Number(sessionStorage.getItem(RELOAD_COUNT_KEY)) || 0;
  if (count >= MAX_RELOADS) {
    notifyManualReload(error);
    return false;
  }
  console.warn('[chunkReload] reloading due to chunk load error', error);
  sessionStorage.setItem(RELOAD_COUNT_KEY, String(count + 1));
  window.location.reload();
  return true;
}

export function installChunkErrorHandlers(router) {
  // Once the page has been alive long enough without further chunk errors,
  // consider the previous reload "settled" and reset the budget. Counter-based
  // rather than time-based on boot, so a slow reload (>5s) on flaky networks
  // doesn't accidentally re-arm a reload loop.
  const settleTimer = setTimeout(() => sessionStorage.removeItem(RELOAD_COUNT_KEY), SETTLED_GRACE_MS);

  router.onError((error) => {
    if (isChunkLoadError(error)) {
      clearTimeout(settleTimer);
      reloadOnceForChunkError(error);
    }
  });

  window.addEventListener('error', (event) => {
    const target = event.target;
    if (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
      const src = target.src || target.href || '';
      if (isOwnAppChunk(src)) {
        clearTimeout(settleTimer);
        reloadOnceForChunkError(new Error(`Asset load failed: ${src}`));
      }
      return;
    }
    if (isChunkLoadError(event.error)) {
      clearTimeout(settleTimer);
      reloadOnceForChunkError(event.error);
    }
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      clearTimeout(settleTimer);
      reloadOnceForChunkError(event.reason);
    }
  });
}
